/**
 * API Key Management Component
 *
 * Manage API keys for external integrations.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  VpnKey as KeyIcon,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { userApiKeyApi } from '../../services/api';
import { ApiKeyResponse, ApiKeyCreateResponse, ApiKeyCreate } from '../../types/auth';

const ApiKeyManagement: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKeyResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Create API Key Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState<ApiKeyCreate>({
    description: '',
    expires_at: undefined,
  });

  // New API Key Display Dialog
  const [newApiKey, setNewApiKey] = useState<ApiKeyCreateResponse | null>(null);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  // Delete Confirmation Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<ApiKeyResponse | null>(null);

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await userApiKeyApi.list();
      setApiKeys(response.api_keys);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    setError('');
    try {
      const newKey = await userApiKeyApi.create(createForm);
      setNewApiKey(newKey);
      setShowKeyDialog(true);
      setCreateDialogOpen(false);
      setCreateForm({ description: '', expires_at: undefined });
      await loadApiKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create API key');
    }
  };

  const handleDeleteApiKey = async () => {
    if (!keyToDelete) return;

    try {
      await userApiKeyApi.delete(keyToDelete.id);
      setSuccess('API key deleted successfully');
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete API key');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            API Keys
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage API keys for external integrations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
            },
          }}
        >
          Create API Key
        </Button>
      </Box>

      {/* Alert Messages */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* API Keys Table */}
      <Paper elevation={3} sx={{ borderRadius: 2 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : apiKeys.length === 0 ? (
          <Box textAlign="center" py={8}>
            <KeyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No API Keys Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Create your first API key to start integrating with external services
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create API Key
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Key Prefix</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Description</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Created</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Last Used</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Expires</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <KeyIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontFamily="monospace">
                          {key.key_prefix}...
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {key.description || <em>No description</em>}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {key.is_active && !isExpired(key.expires_at) ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Active"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<Cancel />}
                          label={isExpired(key.expires_at) ? 'Expired' : 'Inactive'}
                          color="error"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(key.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {key.last_used_at ? formatDate(key.last_used_at) : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={isExpired(key.expires_at) ? 'error' : 'text.primary'}
                      >
                        {key.expires_at ? formatDate(key.expires_at) : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete API Key">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => {
                            setKeyToDelete(key);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Refresh Button */}
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button startIcon={<RefreshIcon />} onClick={loadApiKeys} disabled={isLoading}>
          Refresh
        </Button>
      </Box>

      {/* Create API Key Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <TextField
              fullWidth
              label="Description (Optional)"
              placeholder="e.g., Production integration"
              value={createForm.description}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, description: e.target.value }))
              }
              margin="normal"
              helperText="Help identify what this key is used for"
            />
            <TextField
              fullWidth
              type="datetime-local"
              label="Expiration Date (Optional)"
              value={createForm.expires_at || ''}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  expires_at: e.target.value || undefined,
                }))
              }
              margin="normal"
              helperText="Leave empty for keys that never expire"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: new Date().toISOString().slice(0, 16), // Disable past dates
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateApiKey}>
            Create API Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Show New API Key Dialog */}
      <Dialog
        open={showKeyDialog}
        onClose={() => setShowKeyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" />
            API Key Created Successfully
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Important:</strong> Copy this API key now. You won't be able to see it again!
          </Alert>

          <TextField
            fullWidth
            label="Your API Key"
            value={newApiKey?.api_key || ''}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                    <IconButton
                      onClick={() => copyToClipboard(newApiKey?.api_key || '')}
                      edge="end"
                    >
                      {copied ? <CheckCircle color="success" /> : <CopyIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{
              '& input': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              },
            }}
          />

          <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              <strong>Key Prefix:</strong> {newApiKey?.key_prefix}
            </Typography>
            {newApiKey?.description && (
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                <strong>Description:</strong> {newApiKey.description}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              <strong>Created:</strong> {newApiKey && formatDate(newApiKey.created_at)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setShowKeyDialog(false)}>
            I've Saved the Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete API Key?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this API key? This action cannot be undone.
          </Typography>
          {keyToDelete && (
            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="caption" display="block">
                <strong>Prefix:</strong> {keyToDelete.key_prefix}
              </Typography>
              {keyToDelete.description && (
                <Typography variant="caption" display="block">
                  <strong>Description:</strong> {keyToDelete.description}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteApiKey}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApiKeyManagement;
